<template>
  <div v-if="showOverlay" class="overlay-ad">
    <div class="overlay-backdrop" @click="closeOverlay"></div>
    <div class="overlay-dialog">
      <div class="overlay-content">
        <!-- 加载指示器 -->
        <div v-if="!imageLoaded" class="image-loader">
          <div class="spinner"></div>
          <p class="loading-text">載入中...</p>
        </div>

        <!-- 广告图片 -->
        <a
          v-if="imageLoaded"
          :href="`${currentAd.link}${queryString ? '?' + queryString : ''}`"
          target="_blank"
          class="ad-image-link"
          @click="handleAdClick"
        >
          <img
            :src="`${imageUrl}/${currentAd.image}`"
            :alt="currentAd.name || 'Advertisement'"
            class="ad-image"
          />
        </a>

        <!-- 隐藏的图片用于加载检测 -->
        <img
          :src="`${imageUrl}/${currentAd.image}`"
          :alt="currentAd.name || 'Advertisement'"
          style="display: none;"
          @load="onImageLoad"
          @error="onImageError"
        />
        <button class="close-button" @click="closeOverlay">
          關閉
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import userTrackingService from '@/api/userTracking'

export default {
  name: 'OverlayAd',
  props: {
    overlayAds: {
      type: Array,
      default: () => []
    },
    imageUrl: {
      type: String,
      required: true
    },
    queryString: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showOverlay: false,
      currentAdIndex: 0,
      imageLoaded: false,
      imageError: false
    }
  },
  computed: {
    currentAd() {
      return this.overlayAds[this.currentAdIndex] || {}
    }
  },
  watch: {
    overlayAds: {
      handler(newAds) {
        if (newAds && newAds.length > 0) {
          this.showOverlay = true
          this.currentAdIndex = 0
          this.imageLoaded = false
          this.imageError = false
        }
      },
      immediate: true
    }
  },
  methods: {
    async handleAdClick() {
      const isDebug = process.env.NODE_ENV !== 'production'

      // 從廣告 URL 提取 gameId
      const gameId = userTrackingService.extractGameIdFromUrl(this.currentAd.link)

      if (isDebug) {
        console.debug('[OverlayAd] 廣告被點擊', {
          adName: this.currentAd.name,
          adLink: this.currentAd.link,
          extractedGameId: gameId
        })
      }

      try {
        // 追蹤下載事件 (從 URL 提取 gameId)
        await userTrackingService.trackDownload({
          gameId
        })

        if (isDebug)
          console.debug('[OverlayAd] 追蹤記錄已建立')
      }
      catch (error) {
        if (isDebug)
          console.error('[OverlayAd] 追蹤記錄建立失敗，但不阻擋廣告點擊', { error })

        // User tracking failed, but don't block the ad click
      }
    },
    closeOverlay() {
      this.showOverlay = false
    },
    showNextAd() {
      if (this.overlayAds.length > 1)
        this.currentAdIndex = (this.currentAdIndex + 1) % this.overlayAds.length
    },
    onImageLoad() {
      this.imageLoaded = true
      this.imageError = false
    },
    onImageError() {
      this.imageError = true
      this.imageLoaded = false
    }
  }
}
</script>

<style lang="scss" scoped>
.overlay-ad {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
}

.overlay-dialog {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  z-index: 10000;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(10px);
}

.ad-image-link {
  display: block;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
}

.ad-image {
  max-width: 100%;
  max-height: 70vh;
  min-width: 200px;
  min-height: 100px;
  object-fit: contain;
  display: block;
  border-radius: 12px;
  border: 6px solid #e0e0e0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: #d0d0d0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
}

.image-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 100px;
  padding: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 12px;
  border: 6px solid #e0e0e0;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: shimmer 2s infinite;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(0, 123, 255, 0.1);
    border-top: 3px solid #007bff;
    border-right: 3px solid rgba(0, 123, 255, 0.3);
    border-bottom: 3px solid rgba(0, 123, 255, 0.2);
    border-radius: 50%;
    animation: spin 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
    margin-bottom: 12px;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      border: 2px solid rgba(0, 123, 255, 0.1);
      border-radius: 50%;
      animation: pulse 2s ease-in-out infinite;
    }
  }

  .loading-text {
    color: #495057;
    font-size: 15px;
    font-weight: 600;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    letter-spacing: 0.5px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}


@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.4;
  }
}

.close-button {
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #ff4444 0%, #ff6666 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 68, 68, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: linear-gradient(135deg, #ff6666 0%, #ff8888 100%);
    transform: translateX(-50%) translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 68, 68, 0.4);
  }

  &:active {
    transform: translateX(-50%) translateY(-1px);
  }
}

@media (max-width: 768px) {
  .close-button {
    bottom: -45px;
    padding: 10px 20px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .close-button {
    bottom: -40px;
    padding: 8px 16px;
    font-size: 13px;
  }
}
</style>